// Nadrama <https://nadrama.com>
// Copyright Nadrama Pty Ltd
// All rights reserved.
import { For, Show, createSignal } from "solid-js";
import { useQueryClient } from "@tanstack/solid-query";
import type { SessionBody } from "./api/generated";
import { revokeSession } from "./api/generated";
import { apiErrorMessage, authSlotHeaders } from "./api/runtime";
import { Heading } from "./components/layout";
import { formatDate } from "./format";

type Props = { slot: number; account: string; sessions: SessionBody[] };

// SessionsPage lists session activity and revokes sessions other than this one.
export function SessionsPage(p: Props) {
  const queryClient = useQueryClient();
  const [revoking, setRevoking] = createSignal("");
  const [error, setError] = createSignal("");
  const revoke = async (session: SessionBody) => {
    if (!confirm(`Revoke the ${session.name} session?`)) return;
    setError("");
    setRevoking(session.id);
    try {
      await revokeSession({
        throwOnError: true,
        responseStyle: "fields",
        path: { account: p.account, id: session.id },
        headers: authSlotHeaders(p.slot),
      });
      await queryClient.invalidateQueries({
        queryKey: [p.slot, p.account, "sessions"],
      });
    } catch (reason) {
      setError(apiErrorMessage(reason));
    } finally {
      setRevoking("");
    }
  };
  return (
    <>
      <Heading
        title="Sessions"
        copy="Review and revoke browser and CLI sessions signed in to your user."
      />
      <Show when={error()}>
        <div role="alert" class="alert alert-error mb-4">
          {error()}
        </div>
      </Show>
      <div class="desktop-table panel overflow-x-auto">
        <table class="table">
          <thead>
            <tr>
              <th>Device</th>
              <th>Last active</th>
              <th>Created</th>
              <th>Expires</th>
              <th>Status</th>
              <th class="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            <For each={p.sessions}>
              {(session) => (
                <tr>
                  <td class="font-medium">
                    {session.name}
                    <Show when={session.current}>
                      <span class="badge badge-outline ml-2">Current</span>
                    </Show>
                  </td>
                  <td>{formatDate(session.lastUsedAt)}</td>
                  <td>{formatDate(session.createdAt)}</td>
                  <td>{formatDate(session.expiresAt)}</td>
                  <td>
                    <span
                      class={`status ${session.active ? "text-success" : "text-slate-500"}`}
                    >
                      {session.status}
                    </span>
                  </td>
                  <td class="text-center">
                    <Show
                      when={session.active && !session.current}
                      fallback={<span class="text-slate-400">—</span>}
                    >
                      <button
                        type="button"
                        class="btn btn-ghost btn-sm text-error"
                        disabled={revoking() === session.id}
                        onClick={() => revoke(session)}
                      >
                        {revoking() === session.id ? "Revoking…" : "Revoke"}
                      </button>
                    </Show>
                  </td>
                </tr>
              )}
            </For>
          </tbody>
        </table>
      </div>
      <div class="mobile-card gap-3">
        <For each={p.sessions}>
          {(session) => (
            <article class="panel p-4">
              <div class="flex items-center gap-2">
                <h2 class="font-bold">{session.name}</h2>
                <Show when={session.current}>
                  <span class="badge badge-outline">Current</span>
                </Show>
              </div>
              <dl class="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                <dt class="text-slate-500">Last active</dt>
                <dd>{formatDate(session.lastUsedAt)}</dd>
                <dt class="text-slate-500">Created</dt>
                <dd>{formatDate(session.createdAt)}</dd>
                <dt class="text-slate-500">Expires</dt>
                <dd>{formatDate(session.expiresAt)}</dd>
                <dt class="text-slate-500">Status</dt>
                <dd>{session.status}</dd>
              </dl>
              <Show when={session.active && !session.current}>
                <button
                  type="button"
                  class="btn btn-ghost btn-sm mt-4 text-error"
                  disabled={revoking() === session.id}
                  onClick={() => revoke(session)}
                >
                  {revoking() === session.id ? "Revoking…" : "Revoke"}
                </button>
              </Show>
            </article>
          )}
        </For>
      </div>
    </>
  );
}
